/**
 * AccountActions — barra de acoes consistente pra qualquer view que mostra
 * uma AccountItem (Home AccountTile, ClientDetail AccountRow, EditClient
 * AccountCard).
 *
 * Pattern Apple HIG: hierarquia clara entre primary (filled, com flow) e
 * utility (ghost, direct action sem flow). Console readonly e sempre visivel
 * pra contas bootstrapadas; disabled com tooltip pra nao-bootstrapadas
 * (visibility > hidden).
 *
 * Acoes:
 * - Console (ghost, direct): POST /accounts/{id}/readonly-console -> nova tab
 * - Solicitar (filled value, flow): Link pra /request?account={id}&client={slug}
 *
 * Densidades:
 * - compact (size='xs'): Home grid, ClientDetail row
 * - comfortable (size='sm'): Admin EditClient expanded card
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowSquareOut, TerminalWindow } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { AwsCliConfigModal } from '@/components/ui/AwsCliConfigModal';
import { Button } from '@/components/ui/Button';
import { buttonVariants } from '@/components/ui/button-variants';
import { cn } from '@/lib/utils/cn';
import { jitApi } from '@/lib/api';
import type { AccountItem } from '@/lib/api/types';

export type AccountActionsDensity = 'compact' | 'comfortable';

interface AccountActionsProps {
  account: AccountItem;
  clientSlug: string;
  density?: AccountActionsDensity;
  /** Quando true, esconde o Solicitar (uso em telas onde so faz sentido console). */
  hideRequest?: boolean;
  /** className extra no wrapper (alignment, gap, etc). */
  className?: string;
}

export function AccountActions({
  account,
  clientSlug,
  density = 'compact',
  hideRequest = false,
  className,
}: AccountActionsProps) {
  const [consoleLoading, setConsoleLoading] = useState(false);
  const [cliConfigOpen, setCliConfigOpen] = useState(false);
  const size = density === 'comfortable' ? 'sm' : 'xs';
  const isBootstrapped = Boolean(account.readonly_role_arn);
  const requestHref = `/request?client=${encodeURIComponent(clientSlug)}&account=${encodeURIComponent(account.account_id)}`;

  const handleConsole = async () => {
    if (!isBootstrapped) return;
    setConsoleLoading(true);
    try {
      const result = await jitApi.getReadonlyConsoleUrl(account.account_id);
      // Mesmo gate de seguranca do OpenConsoleButton: validar URL antes de navegar.
      let url: URL;
      try {
        url = new URL(result.console_url);
      } catch {
        throw new Error('Resposta invalida do backend (URL malformada).');
      }
      if (url.protocol !== 'https:') {
        throw new Error(`Protocolo inesperado: ${url.protocol}`);
      }
      if (url.hostname !== 'signin.aws.amazon.com') {
        throw new Error(`Host inesperado: ${url.hostname}`);
      }
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Falha ao gerar URL.';
      toast.error('Nao foi possivel abrir console ReadOnly', {
        description: msg,
        action: {
          label: 'Usar CLI',
          onClick: () => {
            navigator.clipboard?.writeText(
              `aws sts assume-role --role-arn ${account.readonly_role_arn ?? `arn:aws:iam::${account.account_id}:role/aura`} --role-session-name aura-readonly-manual`,
            );
            toast.success('Comando copiado pra clipboard.');
          },
        },
      });
    } finally {
      setConsoleLoading(false);
    }
  };

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <Button
        type="button"
        variant="ghost"
        size={size}
        loading={consoleLoading}
        disabled={!isBootstrapped}
        onClick={handleConsole}
        title={
          isBootstrapped
            ? 'Abrir console AWS (ReadOnly, sem aprovacao)'
            : 'Aguardando bootstrap cross-account'
        }
        aria-label={
          isBootstrapped
            ? `Abrir console ReadOnly da conta ${account.name}`
            : `Console ReadOnly indisponivel: conta ${account.name} sem bootstrap`
        }
      >
        <ArrowSquareOut size={size === 'sm' ? 13 : 12} weight="bold" aria-hidden="true" />
        Console
      </Button>

      <Button
        type="button"
        variant="ghost"
        size={size}
        disabled={!isBootstrapped}
        onClick={() => setCliConfigOpen(true)}
        title={
          isBootstrapped ? 'Copiar profile AWS CLI ReadOnly' : 'Aguardando bootstrap cross-account'
        }
        aria-label={
          isBootstrapped
            ? `Copiar config AWS CLI ReadOnly da conta ${account.name}`
            : `Config AWS CLI indisponivel: conta ${account.name} sem bootstrap`
        }
      >
        <TerminalWindow size={size === 'sm' ? 13 : 12} weight="bold" aria-hidden="true" />
        CLI
      </Button>

      <AwsCliConfigModal
        open={cliConfigOpen}
        onClose={() => setCliConfigOpen(false)}
        input={{
          accountId: account.account_id,
          accountName: account.name,
          clientSlug,
          kind: 'readonly',
        }}
      />

      {!hideRequest && (
        <Link
          to={requestHref}
          className={cn(buttonVariants({ variant: 'value', size }))}
          aria-label={`Solicitar acesso elevado a conta ${account.name}`}
        >
          Solicitar
          <ArrowRight size={size === 'sm' ? 13 : 12} weight="bold" aria-hidden="true" />
        </Link>
      )}
    </div>
  );
}
